import { LearningPathsApi } from "@simbelapp/order-sdk";
import { useLearningPathAttendeesStore } from "./learning-path-attendees.store";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { usePagination } from "~/composables/paginations/usePagination";
import { useLearningPathValidation } from "~/composables/learning-path/useLearningPathValidation";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { LearningPathNavEnum, LearningPathTabTypeEnum } from "~/utils/enums/learning-path.enums";
import type { IHeaderNav, IPaginationApi } from "~/utils/interfaces/table-interfaces";
import type { ILearningPath, ILearningPathStore } from "~/utils/interfaces/learning-path-interfaces";
import LearningPathTrainingsTab from "~/components/Pages/learning-path/component/trainings/TrainingsTab.vue";
import LearningPathGeneralTab from "~/components/Pages/learning-path/component/trainings/GeneralTab.vue";

export const useLearningPathStore = defineStore("learningPathStore", {
  state: (): ILearningPathStore => ({
    currentNav: null,
    learningPath: null,
    learningPathList: null,
    learningPathModal: false,
    learningPathExitModal: false,
    learningPathDeleteUnauthorizedModal: false,
    learningPathArchivedModal: false,
    learningPathIdToArchive: null,
    isEdit: false,
    hasAttendees: false,
    showAddTraining: false,
    form: {
      general: {
        name: "",
        description: "",
      },
      trainings: [],
    },
  }),
  actions: {
    async fetchLearningPath(learningPathId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsApi);
        const data = await apiInstance.getLearningPath({
          learningPathId,
        });
        if (data) {
          this.setLearningPath(data);
        }
      } catch {
        this.setLearningPath(null);
      }
    },
    setLearningPath(learningPath: any | null) {
      this.learningPath = learningPath;
    },
    setLearningPathList(learningPathList: IPaginationApi<ILearningPath> | null) {
      if (learningPathList?.data?.length) {
        this.learningPathList = learningPathList;
      } else {
        this.learningPathList = null;
      }
    },
    setIsEdit(isEdit: boolean) {
      this.isEdit = isEdit;
    },
    async fetchLearningPathList() {
      try {
        const pagination = usePagination(PaginationKeysEnum.LEARNING_PATH_PAGINATION);
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsApi);
        const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
        const paginateQuery = new URLSearchParams(paginateOptions);
        const transformQuery = Object.fromEntries(paginateQuery.entries());
        const result = await apiInstance.getLearningPaths({
          search: pagination.paginateOptions.value.search ?? "",
          ...transformQuery,
        });
        if (result) {
          this.setLearningPathList(result as IPaginationApi<any>);
        }
      } catch {
        this.setLearningPathList(null);
      }
    },
    setCurrentNav(currentNav: IHeaderNav) {
      this.currentNav = currentNav;
    },
    setHasAttendees(hasAttendees: boolean) {
      this.hasAttendees = hasAttendees;
    },
    setShowAddTraining(show: boolean) {
      this.showAddTraining = show;
    },
    handleLearningPathModal(isOpen: boolean) {
      this.learningPathModal = isOpen;
    },
    handleLearningPathExitModal(isOpen: boolean) {
      this.learningPathExitModal = isOpen;
    },
    handleLearningPathArchivedModal(isOpen: boolean) {
      this.learningPathArchivedModal = isOpen;
    },
    handleLearningPathDeleteUnauthorizedModal(isOpen: boolean) {
      this.learningPathDeleteUnauthorizedModal = isOpen;
    },
    navigate(nav: IHeaderNav) {
      this.currentNav = nav;
    },
    resetForm() {
      this.form = {
        general: {
          name: "",
          description: "",
        },
        trainings: [],
      };
    },
    async editLearningPath(learningPathId: string, disabled: boolean) {
      const feedback = useFeedback();

      try {
        await this.loadLearningPathDatas(learningPathId);

        if (this.learningPath?.learningPathId) {
          this.setIsEdit(true);
          this.setHasAttendees(disabled);
          const initTab = {
            name: LearningPathNavEnum.TRAININGS,
            type: LearningPathTabTypeEnum.TRAININGS,
            component: LearningPathTrainingsTab,
          };

          if (initTab) {
            this.setCurrentNav(initTab);
          }

          this.handleLearningPathModal(true);
        }
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async loadLearningPathDatas(learningPathId: string) {
      await this.fetchLearningPath(learningPathId);

      if (this.learningPath?.learningPathId) {
        this.form = {
          general: {
            name: this.learningPath?.name,
            description: this.learningPath?.description,
          },
          trainings: this.learningPath?.trainings,
        };

        this.hasAttendees = this.learningPath?.hasAttendees;
      }
    },
    createLearningPathModal() {
      const initTab = {
        name: LearningPathNavEnum.GENERAL,
        type: LearningPathTabTypeEnum.GENERAL,
        component: LearningPathGeneralTab,
      };

      if (initTab) {
        this.setCurrentNav(initTab);
      }
      this.handleLearningPathModal(true);
      this.setIsEdit(false);
    },
    async resetModal(fetch: boolean = true) {
      const { resetValidators } = useLearningPathValidation();
      const learningPathAttendeesStore = useLearningPathAttendeesStore();
      const initTab = {
        name: LearningPathNavEnum.TRAININGS,
        type: LearningPathTabTypeEnum.TRAININGS,
        component: LearningPathTrainingsTab,
      };
      const router = useRouter();
      const route = useRoute();
      const { learning_path_id, ...restQuery } = route.query;

      this.setLearningPath(null);
      this.handleLearningPathModal(false);
      this.setIsEdit(false);
      this.setHasAttendees(false);
      this.setShowAddTraining(false);
      this.resetForm();

      /** RESET LEARNING PATH ATTENDEES STORE */
      learningPathAttendeesStore.setCurrentLearningPathId(null);
      learningPathAttendeesStore.setLearningPath(null);

      resetValidators();

      if (initTab) {
        this.setCurrentNav(initTab);
      }

      if (fetch) {
        await this.fetchLearningPathList();
      }

      await router.push({
        query: restQuery,
      });
    },
    async deleteLearningPath(learningPathId: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsApi);
        await apiInstance.archiveLearningPath({
          learningPathId,
        });
        feedback.success("Le parcours a bien été archivé", "small");
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async createLearningPath() {
      const feedback = useFeedback();
      const { form } = this;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsApi);
        await apiInstance.createLearningPath({
          createLearningPathDto: {
            name: form.general.name,
            description: form.general.description,
            trainings: form.trainings.map((training) => training.trainingId),
          },
        });

        feedback.success("Le parcours a bien été créé", "small");
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async updateLearningPath() {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsApi);
      try {
        await apiInstance.updateLearningPath({
          learningPathId: this.learningPath?.learningPathId,
          updateLearningPathDto: {
            name: this.form.general.name,
            description: this.form.general.description,
            trainings: this.form.trainings.map((training) => training.trainingId),
          },
        });
        feedback.success("Vos modifications ont bien été sauvegardées", "small");
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async duplicateLearningPath(learningPathId: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LearningPathsApi);
        await apiInstance.duplicateLearningPath({
          duplicateLearningPathDto: {
            originalLearningPathId: learningPathId,
          },
        });
        feedback.success("Parcours de formation dupliqué avec succès", "small");
        await this.fetchLearningPathList();
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
  },
});
